import { Component, Prop, Event, EventEmitter, Method, State, Element, h, forceUpdate } from '@stencil/core';
import ReportHelpers from '../internal/log-modal/report-codes-helper';
import { Color, Size } from '../../interface';
import { AreaData, Trouble, Zone } from './area-interface';
import has from 'lodash-es/has';
import map from 'lodash-es/map';
import forOwn from 'lodash-es/forOwn';
import sortBy from 'lodash-es/sortBy';
import uniq from 'lodash-es/uniq';
import groupBy from 'lodash-es/groupBy';

@Component({
  tag: 'lar-area',
  styleUrl: 'area.scss',
  shadow: true
})
export class Area {
  @Element() el!: HTMLLarAreaElement;
  /////// LarvaNode base properties
  /**
   * Component main icon
   */
  @Prop({ mutable: true }) icon?: string = 'security';
  /**
   * The color to use from your application's color palette.
   * Detrouble options are: `"primary"`, `"secondary"`, `"tertiary"`, `"success"`, `"warning"`, `"danger"`, `"light"`, `"medium"`, and `"dark"`.
   */
  @Prop({ mutable: true }) color?: Color = 'primary';
  /**
   * The color to use from your application's color palette for Components modal window.
   */
  @Prop({ mutable: true }) colorModal?: Color;
  /**
   * The color to use from your application's color palette for inputs
   */
  @Prop({ mutable: true }) colorInputs?: Color;
  /**
   * The color to use from your application's color palette for indication icon
   */
  @Prop() colorIconSmall?: Color;
  /**
   * Allow node indication color automatic change based on feedback/node value. Defaults to false
   */
  @Prop() allowIndicationAutoColoring: boolean = false;
  /**
   * Allow node color automatic change based on feedback/node value. Defaults to true
   */
  @Prop() allowNodeAutoColoring: boolean = true;
  /**
   * Enable quick action for security node
   */
  @Prop() enableSecurityQuickActions: boolean = false;
  /**
   * Hide node titles
   */
  @Prop() hideTitles?: boolean;
  /**
   * Component superscript title
   */
  @Prop() supTitle?: string;
  /**
   * Component subtitle
   */
  @Prop() subTitle?: string;
  /**
   * Is logging for this component enabled (lar-log subcomponent loaded)
   */
  @Prop() log: boolean = false;
  /**
   * Component main title
   */
  @Prop() mainTitle!: string;
  /**
   * Node size
   */
  @Prop() nodeSize?: Size = 'default';
  /**
   * @see {@link ../readme.md} chapter "Components input and output" for further information.
   */
  @Event() output!: EventEmitter;
  /**
   * @see {@link ../readme.md} chapter "Sub-Components requests and responses" for further information.
   */
  @Event() request!: EventEmitter;
  /////// LarvaNode base properties and events - end

  private troubles: Trouble[] = [];
  private zones: Zone[] = [];

  @State() zonesString: string;
  @State() armed: boolean = false;
  @State() zoneopened: boolean = false;
  @State() exitDelayActive: boolean = false;
  @State() entryDelayActive: boolean = false;
  @State() transactionDelay: number = 0;
  @State() alarm: boolean = false;
  @State() troublesString: string;
  @State() loading: boolean = true;

  private transactionDelayTimeout?: number;
  private node!: HTMLLarNodeElement;

  /**
   * Larva input message
   */
  @Method()
  async input(data: any) {
    if (!this.validateData(data)) {
      // this may be some success response ors.
      return;
    }
    // 8.5 Indications - Table 8 – Indication - I&HAS set / I&HAS unset
    this.armed = data.state?.armed ? true : false;
    // 8.5 Indications - Table 8 – Indication - Entry indication (see 8.3.8.2) & Completion of setting (see 8.3.7) & Setting (see 8.3.4)
    this.exitDelayActive = (data.state?.exit_delay_active) ? true : false;
    this.entryDelayActive = (data.state?.entry_delay_active) ? true : false;
    if (this.exitDelayActive || this.entryDelayActive) {
      this.transactionDelay = this.exitDelayActive ? data.state?.exit_delay_expires_in_seconds : data.state?.entry_delay_expires_in_seconds
      this.transactionDelay = this.transactionDelay || 0;
      this.transactionDelay = Math.round(this.transactionDelay);
    }
    // 8.5 Indications - Table 8 – Indication - Hold-up alarm condition, Intruder alarm condition
    this.alarm = (data.state?.alarm) ? true : false;
    // 8.5 Indications - Table 8 – Indication - Fault conditions (see Table 1), Tamper condition, Alert indication
    this.troubles = Array.isArray(data.state?.troubles) ? data.state?.troubles : [];
    // show only unacked troubles
    this.troubles = this.troubles.filter(e => e.acked === null || e.acked === undefined)
    // 8.5 Indications - Table 8 – Indication - Individual intrusion detector indication
    this.zones = Array.isArray(data.zones) ? data.zones : [];
    this.zonesString = JSON.stringify(this.zones);
    this.troublesString = JSON.stringify(this.troubles);
    if (this.troubles.length === 0) {
      // this.hideModal('#troublesmodal');
    }
    this.loading = false;
  }

  /**
   * Larva error input
   */
  @Method()
  async error(data: any) {
    if (this.node) {
      this.node.error(data);
    }
    this.loading = false;
  }

  componentDidLoad() {
    const el = this.el.shadowRoot || this.el;
    this.node = el.querySelector('lar-node') as HTMLLarNodeElement;
    this.output.emit();
  }

  validateData(data: any): data is AreaData {
    if (has(data, 'id') && has(data, 'state') && has(data, 'state.armed')) {
      return true;
    }
    if (!has(data, 'success')) {
      console.error('Area Invalid input message', data);
    }
    return false;
  }

  arm() {
    this.loading = true;
    return this.output.emit({ command: 'arm' });
  }

  partialArm(data: {bypassZones: string[], overrideTroubles: boolean}) {
    this.loading = true;
    this.output.emit({ command: 'arm', override_zones: data.bypassZones, override_troubles: data.overrideTroubles });
  }

  disarm() {
    this.loading = true;
    this.output.emit({ command: 'disarm' });
  }

  render() {
    let value = this.loading ? ' ' : 'area.disarmed';
    let iconSmall = 'unlock';
    let valueTranslationValues;
    let iconSmallColor = this.colorIconSmall;

    if (this.transactionDelayTimeout !== undefined) {
      clearTimeout(this.transactionDelayTimeout);
      this.transactionDelayTimeout = undefined;
    }
    // if ARMED, detect how its armed
    if (this.armed) {
      const bypassedZone = this.zones.find(z => z.overridden === true) ? true : false;
      const bypassedTrouble = this.troubles.find(t => t.overridden === true) ? true : false;
      const newTroubles = this.troubles.find(t => !t.overridden) ? true : false;
      if (bypassedZone && bypassedTrouble) {
        value = 'area.partalArmedWithTrouble';
        iconSmall = 'halflock_trouble';
      } else if (bypassedTrouble) {
        value = 'area.armedWithTouble';
        iconSmall = 'lock_trouble';
      } else if (bypassedZone) {
        value = 'area.partalArmed';
        iconSmall = 'halflock';
      } else {
        value = 'area.armed';
        iconSmall = 'lock';
      }
      if (newTroubles) {
        iconSmall = 'warning';
      }
    }
    // if unarmed and we have troubles, change icon to warning
    if (this.troubles.length > 0 && !this.armed) {
        iconSmall = 'warning';
    }
    // exit delay indiction
    if ((this.exitDelayActive || this.entryDelayActive) && this.transactionDelay > 0) {
      value = this.exitDelayActive? 'area.exitdelay': 'area.entrydelay';
      valueTranslationValues = { seconds: String(this.transactionDelay) };
      this.transactionDelayTimeout = window.setTimeout(() => {
        if (this.transactionDelay === 0) {
          clearTimeout(this.transactionDelayTimeout);
        } else {
          this.transactionDelay--;
        }
        forceUpdate(this.el);
      }, 1000);
    }
    // if we have alarm state
    // detect what alarm we have
    if (this.alarm) {
      const alarms: string[] = [];
      const unresolvedRroubles = this.troubles.filter(t => (t.resolved === '' || t.resolved === null || t.resolved === undefined));
      const intruderAlarm = unresolvedRroubles.find(t => (t.type_id === 0)) ? true : false;
      const zoneAlarms = unresolvedRroubles.filter(t => t.type_id === 3 && !!t.zone?.reportcode);
      const fireAlarm = zoneAlarms.find(t => ReportHelpers.isFireAlarm(t.zone?.reportcode)) ? true : false;
      const medicalAlarm = zoneAlarms.find(t => ReportHelpers.isMedicalAlarm(t.zone?.reportcode)) ? true : false;
      const panicAlarm = zoneAlarms.find(t => ReportHelpers.isPanicAlarm(t.zone?.reportcode)) ? true : false;
      const otherAlarm = zoneAlarms.find(t => ReportHelpers.isOtherAreaAlarm(t.zone?.reportcode)) ? true : false;
      if (intruderAlarm) {
        alarms.push('intruder');
      }
      if (fireAlarm) {
        alarms.push('fire');
      }
      if (medicalAlarm) {
        alarms.push('medical');
      }
      if (panicAlarm) {
        alarms.push('panic');
      }
      if (otherAlarm) {
        alarms.push('other');
      }
      if (alarms.length === 0) {
        alarms.push('other');
      }
      value = alarms.length > 1 ? 'area.alarms.multiple' : 'area.alarms.' + alarms[0];
      iconSmall = alarms.length > 1 ? 'alarm' : alarms[0] + '_alarm';
    }
    if (this.allowIndicationAutoColoring && this.alarm) {
      iconSmallColor = this.allowNodeAutoColoring === true ? 'light' : 'danger'; // node is collored, do not color icon
    } else if (this.allowIndicationAutoColoring) {
      switch(iconSmall) {
        case 'halflock_trouble':
        case 'lock_trouble':
        case 'halflock':
        case 'warning':
          iconSmallColor = 'warning';
          break;
        case 'unlock':
          iconSmallColor = 'danger';
          break;
        case 'lock':
          iconSmallColor = 'success';
          break;
      }
    }

    const componentProps = {
      color: this.colorInputs || this.color,
      colorModal: this.colorModal || this.color,
      zones: this.zonesString,
      troubles: this.troublesString,
      armed: this.armed,
      alarm: this.alarm,
      zoneopened: this.zones.find(z => (z.state === 'open')) ? true : false,
      hastroubles: this.troubles.length > 0,
      icon: this.icon,
      loading: this.loading,
      onArm: () => this.arm(),
      onDisarm: () => this.disarm(),
      onOutput: (evt: CustomEvent) => {
        this.loading = true;
        this.output.emit(evt.detail)
      },
    }

    return [
      <lar-node
        value={value}
        hideTitles={this.hideTitles}
        valueTranslationValues={valueTranslationValues}
        iconSmall={iconSmall}
        icon={this.icon}
        colorIconSmall={iconSmallColor}
        color={this.alarm && this.allowNodeAutoColoring === true ? 'danger' : this.color}
        supTitle={this.supTitle}
        mainTitle={this.mainTitle}
        subTitle={this.subTitle}
        colorModal={this.alarm && this.allowNodeAutoColoring === true ? 'danger' : this.colorModal}
        colorInputs={this.colorInputs}
        log={this.log}
        loading={this.loading}
        nodeSize={this.nodeSize}
        component="lar-area-content"
        componentProps={componentProps}
      >
        {this.enableSecurityQuickActions === true &&
          <lar-button-push
            size="small"
            slot="titles"
            color={this.colorInputs || this.color}
            disabled={this.loading || (this.zoneopened && !this.armed) || this.troubles.length > 0}
            onClick={ev => this.armed? this.disarm() : this.arm()}
          />
        }
        <slot />
      </lar-node>,
    ];
  }
}
